module Sm.UI.Timer

open System
open MobF

open Sm.Core.Date

open Sm.Service.Tracking
open Sm.Service.Repository
open Sm.Service.Session

open Sm.Model
open Sm.Model.Record

open Sm.UI.Interaction
open Sm.UI.Activity
open Sm.UI.Form
open Sm.UI.Form.Control
open Sm.UI.Heatmap

type Activity = {
    Elapsed: TimeSpan
    Running: bool
}

type Mode =
    | Idle
    | Programmed
    | Running
    | Paused

type Time =
    | Under of TimeSpan
    | Over of TimeSpan

type StepSource =
    abstract TimerSteps: (StepTime * Record.Type) list


type State = {
    Date: DateOnly
    StepSource: StepSource option
    Activity: Activity option
    Ticks: int64
}

type Msg =
    | ProvideStepSource of StepSource
    | Tick
    | Start
    | Stop
    | Advance
    | Pause
    | Resume
    | HandleCommand of Command

type Computed =
    abstract Steps: (StepTime * Record.Type) list
    abstract Mode: Mode
    abstract Time: Time option
    abstract LeftButton: Button<Msg>
    abstract RightButton: Button<Msg>
    abstract TrackingCharts: Heatmap list
    abstract Active: Record.Type option

type TimerModel = Model<State, Msg, Computed>

let create (ctx: Context) : TimerModel =
    let tracker = Tracker.create ctx.Environment ctx.Repository

    let loadRange date =
        let range = Exact date
        tracker.Post(Tracker.Load range)

    let init () = 
        let today = DateOnly.Today
        loadRange today

        {
            Date = today
            StepSource = None
            Activity = None
            Ticks = 0
        }

    let update (state, post, computed: Computed) msg =
        let applyTracking () =
            match computed.Steps with
            | [] -> ()
            | (_, record) :: _ -> record |> track tracker.Computed.Pending
            tracker.Post(Tracker.Apply)

        let markSession status =
            //Because of the somewhat stateless nature of the timer, and the
            //fact that you can change the list of steps at any point, actually
            //working out when a timer session starts and ends is tricky. The
            //idea here is to mark a session on the last step remaining, whether
            //it is completed or not (this covers the case where pending steps
            //are simply cleared out or cancelled)
            computed.Steps
            |> Seq.tryLast
            |> Option.map snd
            |> Option.iter (fun record ->
                record.Post(MarkSession status)
            )

        match msg with
        | ProvideStepSource source ->
            { state with StepSource = Some source }

        | Tick ->
            let today = DateOnly.Today

            //This is a bit tricky, because we may get this tick when the timer
            //is active, in which case the adjustment is lost. It's necessary to
            //reload the tracker when the date changes, or it will drop *all*
            //adjustments 'from the future'. Because these measurements are not
            //critical - worst case you might lose a second or two if you are
            //tracking around midnight - I'm considering this an acceptable
            //limitation, rather than stressing about buffering them somewhere.
            if state.Date <> today then
                loadRange today
                { state with Date = today }
            else
                match state.Activity with
                | Some activity when activity.Running = true ->
                    let activity = { activity with Elapsed = activity.Elapsed + TimeSpan.OneSecond }

                    match computed.Steps with
                    | [] -> ()
                    | (step, _) :: _ ->
                        let adjustment = 
                            if activity.Elapsed < step.Estimate then SecondsUnderTime 1
                            else SecondsOverTime 1

                        tracker.Post(Tracker.Update adjustment)

                    { state with Activity = Some activity; Ticks = state.Ticks + 1L }
                | _ -> 
                    { state with Ticks = state.Ticks + 1L }

        | Start ->
            match computed.Steps with
            | [] -> state
            | _ ->
                let activity = {
                    Running = true
                    Elapsed = TimeSpan.OneSecond 
                }
                { state with Activity = Some activity }

        | Stop ->
            applyTracking ()
            markSession false

            for (step, record) in computed.Steps do
                if step.Pending 
                then record.Post(ClearStep)

            { state with Activity = None }

        | Advance ->
            applyTracking ()

            if computed.Steps.Length = 1 then
                markSession true

            match computed.Steps with
            | [] -> { state with Activity = None }
            | (_, record) :: _ ->
                let elapsed, running =
                    match state.Activity with
                    | Some activity -> activity.Elapsed, activity.Running
                    | None -> TimeSpan.Zero, false

                record.Post(CompleteStep elapsed)

                if running then post Start
                { state with Activity = None }

        | Pause ->
            applyTracking ()
            match state.Activity with
            | Some activity when activity.Running ->
                let activity = { activity with Running = false }
                { state with Activity = Some activity }
            | _ -> state

        | Resume ->
            match state.Activity with
            | Some activity when not activity.Running ->
                let activity = { activity with Running = true }
                { state with Activity = Some activity }
            | _ -> state

        | HandleCommand TimerStart ->
            match computed.RightButton with
            | { State = { Disabled = false }; Command = msg } -> post msg
            | _ -> ()
            state

        | HandleCommand TimerStop ->
            match computed.LeftButton with
            | { State = { Disabled = false }; Command = msg } -> post msg
            | _ -> ()
            state

    let compute state =
        let button icon msg enabled =
            {
                Content = Icon icon
                Command = msg
                State = { ButtonState.Default with Disabled = not enabled }
            }

        { new Computed with
            member _.Steps =
                state.StepSource 
                |> Option.map _.TimerSteps
                |> Option.defaultValue []

            member this.Mode =
                match state.Activity with
                | Some activity -> if activity.Running then Running else Paused
                | None -> if this.Steps.IsEmpty then Idle else Programmed

            member this.Time =
                match this.Steps with
                | (step, _) :: _ ->
                    let elapsed =
                        match state.Activity with
                        | Some activity -> activity.Elapsed
                        | None -> TimeSpan.Zero

                    Some (
                        if elapsed > step.Estimate 
                            then Over (elapsed - step.Estimate)
                        else
                            let remaining = step.Estimate - elapsed
                            Under remaining
                    )
                | _ -> None

            member this.LeftButton =
                match this.Mode with
                | Idle -> Cross, Stop, false
                | Programmed -> Cross, Stop, true
                | Running -> IconName.Pause, Pause, true
                | Paused -> Cross, Stop, true
                |||> button

            member this.RightButton =
                match this.Mode with
                | Idle -> Play, Start, false
                | Programmed -> Play, Start, true
                | Running ->
                    let icon = if this.Steps.Length <= 1 then Trophy else IconName.Tick
                    icon, Advance, true
                | Paused -> Play, Resume, true
                |||> button

            member _.TrackingCharts =
                state.Ticks |> ignore
                hourSeries DateTime.Now tracker.Computed.Sample

            member this.Active =
                match this.Steps, state.Activity with
                | (_, r) :: _, Some _ -> Some r
                | _ -> None
        }

    Model.useInit init
    |> Model.andUpdateWithComputed update
    |> Model.createWithComputed compute
